import { useEffect, useMemo, useState } from "react"
import { Avatar, Button, Checkbox, Text } from "../atoms"
import { CardListItem, Input } from "../molecules"
import { useModal } from "../molecules/Modal/ModalProvider"
import { getMentors, setSpecialistMentors } from "@/services/mentoringServices"
import { MentorsDataType } from "@/types/mentoring"
import { useAuth } from "@/contexts/AuthContext"
import { useToast } from "@/hooks/useToast"

interface AddSpecialistMentorsModalContentProps {
  companyId: string
  excludedMentorIds: number[]
  onSuccess: () => void
}

export function AddSpecialistMentorsModalContent({
  companyId,
  excludedMentorIds,
  onSuccess,
}: AddSpecialistMentorsModalContentProps) {
  const { user } = useAuth()
  const showToast = useToast()
  const { hideModal } = useModal()

  const [mentors, setMentors] = useState<MentorsDataType[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  useEffect(() => {
    setLoading(true)
    getMentors({ emp_id: companyId })
      .then(({ data }) => setMentors(data))
      .catch((error) => console.error("Error loading mentors:", error))
      .finally(() => setLoading(false))
  }, [companyId])

  const candidates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return mentors
      .filter((mentor) => !excludedMentorIds.includes(mentor.mentor_id as number))
      .filter((mentor) => !term || mentor.nome.toLowerCase().includes(term))
  }, [mentors, excludedMentorIds, searchTerm])

  const toggleSelected = (mentorId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(mentorId)) {
        next.delete(mentorId)
      } else {
        next.add(mentorId)
      }
      return next
    })
  }

  const handleConfirm = async () => {
    setSubmitting(true)

    const results = await Promise.allSettled(
      Array.from(selectedIds).map((mentorId) =>
        setSpecialistMentors(user.emp_id, mentorId, parseInt(companyId))
      )
    )

    setSubmitting(false)

    const succeeded = results.filter((result) => result.status === "fulfilled").length
    const failed = results.length - succeeded

    showToast({
      message:
        failed === 0
          ? `${succeeded} mentor(es) vinculado(s) com sucesso!`
          : `${succeeded} de ${results.length} mentores vinculados; ${failed} falharam.`,
      variant: failed === 0 ? "success" : succeeded === 0 ? "error" : "warning",
    })

    onSuccess()
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <Input
        id="add-specialist-mentors-search"
        placeholder="Buscar mentor por nome..."
        iconLeft="search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <div className="flex max-h-[400px] flex-col gap-2 overflow-y-auto pr-2">
        {loading && (
          <Text
            variant="body"
            className="py-8 text-center text-dark-300"
          >
            Carregando mentores...
          </Text>
        )}

        {!loading && candidates.length === 0 && (
          <Text
            variant="body"
            className="py-8 text-center text-dark-300"
          >
            Nenhum mentor encontrado.
          </Text>
        )}

        {candidates.map((mentor) => {
          const mentorId = mentor.mentor_id as number
          const isSelected = selectedIds.has(mentorId)

          return (
            <CardListItem
              key={mentorId}
              cols={3}
              className="cursor-pointer"
            >
              <div className="col-span-3 flex items-center gap-3">
                <Checkbox
                  id={`mentor-${mentorId}`}
                  checked={isSelected}
                  onChange={() => toggleSelected(mentorId)}
                />
                <label
                  htmlFor={`mentor-${mentorId}`}
                  className="flex flex-1 cursor-pointer items-center gap-3"
                >
                  <Avatar
                    size="md"
                    alt={mentor.nome}
                  />
                  <Text variant="body">{mentor.nome}</Text>
                </label>
              </div>
            </CardListItem>
          )
        })}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-white/5 p-4">
        <Text
          variant="body"
          className="text-dark-300"
        >
          {selectedIds.size} selecionado(s)
        </Text>

        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => hideModal()}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || selectedIds.size === 0}
          >
            {submitting ? "Vinculando..." : "Adicionar"}
          </Button>
        </div>
      </div>
    </div>
  )
}
